import type {
  DSNCAdClassed,
  DSNCLinkedButtons,
  DSNCQuote,
  DSNCString,
  DSResource,
} from 'shared-definitions/types'

export interface MixInClassedAdsOpts {
  ad: string | null
  video: string | null
  maxAdInserts?: number
  insertAdEveryDesktop?: number
  insertAdEveryMobile?: number
  firstDesktopAd?: 'video' | 'plain'
}

type IMixInClassedAdsReturnType<T> = T | DSNCAdClassed

export function mixInClassedAds<T extends DSResource | DSNCString | DSNCLinkedButtons | DSNCQuote>(
  input: readonly T[],
  opts: MixInClassedAdsOpts
): readonly IMixInClassedAdsReturnType<T>[] {
  const maxAdInserts = opts.maxAdInserts ?? 20
  const insertAdEveryDesktop = opts.insertAdEveryDesktop ?? 5
  const insertAdEveryMobile = opts.insertAdEveryMobile ?? 3
  const firstDesktopAd = opts.firstDesktopAd ?? 'plain'

  const isEmptyParagraph = ({ html }: DSNCString): boolean => {
    if (!html) {
      return false
    }
    return html.replace(/(<([^>]+)>)/gi, '').length === 0
  }

  const isAvoidPlace = (index: number, list: readonly T[], threshold = 1): boolean => {
    const isAvoidItem = (item: IMixInClassedAdsReturnType<T>): boolean =>
      item.resource === 'nc-quote' || item.resource === 'nc-linked-buttons'

    for (let i = index - threshold + 1, to = index + threshold; i <= to; i++) {
      const block = list[i]

      if (
        i > 0 &&
        i < list.length &&
        (isAvoidItem(block) || isEmptyParagraph(block as DSNCString))
      ) {
        return true
      }
    }

    return false
  }

  let counter = 0
  let insertAdOrderDesktopNum = 1
  let insertAdOrderMobileNum = 1
  const result: (T | DSNCAdClassed)[] = []

  const insertAd = (
    className: string,
    shouldInsertDesktop: boolean,
    shouldInsertMobile: boolean,
    type: DSNCAdClassed['type']
  ): void => {
    if (shouldInsertDesktop) {
      result.push({ resource: 'nc-ad-classed', className, visible: 'desktop', type })
      insertAdOrderDesktopNum++
    }
    if (shouldInsertMobile) {
      result.push({ resource: 'nc-ad-classed', className, visible: 'mobile', type })
      insertAdOrderMobileNum++
    }
  }

  const insertFirstAd = (params: MixInClassedAdsOpts): void => {
    if (params.video) {
      result.push({
        resource: 'nc-ad-classed',
        className: params.video,
        visible: 'mobile',
        type: 'video',
      })
    }
    if (params.video && firstDesktopAd === 'video') {
      result.push({
        resource: 'nc-ad-classed',
        className: params.video,
        visible: 'desktop',
        type: 'video',
      })
    } else if (params.ad) {
      result.push({
        resource: 'nc-ad-classed',
        className: params.ad,
        visible: 'desktop',
        type: 'plain',
      })
    }
    insertAdOrderDesktopNum++
    insertAdOrderMobileNum++
  }

  for (let i = 0, block = input[i]; i < input.length; block = input[++i]) {
    result.push(block)

    const isParagraph = block.resource === 'nc-string'
    const hasHeading = /<H\d>/i.test((block as DSNCString).html)
    const shouldInsert =
      !maxAdInserts || maxAdInserts > Math.min(insertAdOrderDesktopNum, insertAdOrderMobileNum)

    if (isParagraph && !isEmptyParagraph(block as DSNCString) && !hasHeading && shouldInsert) {
      counter++
      const shouldInsertDesktop = counter % insertAdEveryDesktop === 0
      const shouldInsertMobile = counter % insertAdEveryMobile === 0

      if ((shouldInsertDesktop || shouldInsertMobile) && !isAvoidPlace(i, input)) {
        if (insertAdOrderDesktopNum === 1 || insertAdOrderMobileNum === 1) {
          // first of ad blocks on mobile should be a video
          insertFirstAd(opts)
        } else if (opts.ad) {
          insertAd(opts.ad, shouldInsertDesktop, shouldInsertMobile, 'plain')
        }
      }
    }
  }

  return result
}
